/**
 * Walkmore-only dynamic pricing A/B (static week-2 vs tier-based live prices).
 * Not used in aaron_funnel — Join uses category=join and separate plan scripts.
 */
export const AB_DYNAMIC_PRICING_STORAGE_KEY = 'mt_ab_dynamic_pricing';

export type DynamicPricingVariant = 'static' | 'dynamic';

const VALID_VARIANTS: readonly DynamicPricingVariant[] = ['static', 'dynamic'];

const COOKIE_MAX_AGE_SEC = 365 * 24 * 60 * 60;

function isValidVariant(value: string): value is DynamicPricingVariant {
    return (VALID_VARIANTS as readonly string[]).includes(value);
}

function readCookie(): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(
        new RegExp(`(?:^|; )${AB_DYNAMIC_PRICING_STORAGE_KEY.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`)
    );
    return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(variant: DynamicPricingVariant): void {
    if (typeof document === 'undefined') return;
    document.cookie = `${AB_DYNAMIC_PRICING_STORAGE_KEY}=${encodeURIComponent(variant)};path=/;max-age=${COOKIE_MAX_AGE_SEC};SameSite=Lax`;
}

function readPersistedVariant(): DynamicPricingVariant | null {
    if (typeof localStorage !== 'undefined') {
        const fromStorage = localStorage.getItem(AB_DYNAMIC_PRICING_STORAGE_KEY);
        if (fromStorage && isValidVariant(fromStorage)) {
            return fromStorage;
        }
    }

    const fromCookie = readCookie();
    if (fromCookie && isValidVariant(fromCookie)) {
        return fromCookie;
    }

    return null;
}

function persistVariant(variant: DynamicPricingVariant): void {
    if (typeof localStorage !== 'undefined') {
        localStorage.setItem(AB_DYNAMIC_PRICING_STORAGE_KEY, variant);
    }
    writeCookie(variant);
}

export function getDynamicPricingVariant(): DynamicPricingVariant | null {
    return readPersistedVariant();
}

/** Walkmore uses classic static intro prices ($4.99 / $17.99 / $29.99). */
export function ensureDynamicPricingVariant(): DynamicPricingVariant {
    const existing = readPersistedVariant();
    if (existing === 'static') {
        return existing;
    }
    persistVariant('static');
    return 'static';
}

export function isDynamicPricingEnabled(): boolean {
    return false;
}

export function getDynamicPricingAnalyticsProps(): Record<string, string | number> {
    return {
        pricing_mode: 'static',
        pricing_shadow_mode: 1,
    };
}
